import { getPitcherEligibility, PitchingLogEntry, PitchCountRuleset } from './pitch-count-rules';
import { Player } from '../types/dugout';

export interface PitchingIntel {
  isEligible: boolean;
  color: 'green' | 'amber' | 'red';
  label: string;
  pitchesRemaining: number;
  percent: number;
  dailyMax: number;
  reason?: string;
  // Explicit per-scope figures so a tournament UI can show BOTH a daily line and
  // a tournament line at once, instead of the single collapsed `label`/`percent`.
  dailyRemaining: number;        // pitches left today
  dailyUsed: number;             // pitches thrown today (incl. current game)
  eventMax: number | null;       // tournament cap, null if none
  eventRemaining: number | null; // pitches left this tournament, null if no cap
  eventUsed: number | null;      // pitches thrown this tournament (incl. current game)
}

export function getPitchingIntel(
  pitchingLog: PitchingLogEntry[] = [],
  rules: PitchCountRuleset,
  ageGroup: string,
  gameDate: Date,
  currentGamePitchCount: number = 0,
  currentTournamentId?: string,
  enforceGlobalRest: boolean = true,
  customRules?: any
): PitchingIntel {
  const eligibility = getPitcherEligibility(
    pitchingLog,
    rules,
    ageGroup,
    gameDate,
    currentGamePitchCount,
    currentTournamentId,
    enforceGlobalRest,
    customRules
  );

  let color: 'green' | 'amber' | 'red' = 'green';
  let label = `${eligibility.pitchesRemaining} pitches remaining`;
  let percent = 0;

  if (eligibility.eventMax) {
    const totalInEvent = (eligibility.totalPitchesInEvent || 0) + currentGamePitchCount;
    percent = (totalInEvent / eligibility.eventMax) * 100;
    
    label = `${eligibility.pitchesRemainingInEvent} pitches remaining in tournament`;

    if (percent > 80 || !eligibility.eligible) {
      color = 'red';
    } else if (percent > 50) {
      color = 'amber';
    } else {
      color = 'green';
    }
  } else {
    // Fallback to daily max logic for color if no event max
    const totalToday = (eligibility.dailyMax - eligibility.pitchesRemaining);
    percent = (totalToday / eligibility.dailyMax) * 100;
    
    if (percent > 80 || !eligibility.eligible) {
      color = 'red';
    } else if (percent > 50) {
      color = 'amber';
    } else {
      color = 'green';
    }
  }

  // Per-scope figures for a two-line tournament readout.
  const dailyUsed = Math.max(0, eligibility.dailyMax - eligibility.pitchesRemaining);
  const eventMax = eligibility.eventMax ?? null;
  const eventUsed = eventMax != null ? (eligibility.totalPitchesInEvent || 0) + currentGamePitchCount : null;
  const eventRemaining = eventMax != null
    ? (eligibility.pitchesRemainingInEvent ?? Math.max(0, eventMax - (eventUsed || 0)))
    : null;

  return {
    isEligible: eligibility.eligible,
    color,
    label,
    pitchesRemaining: eligibility.pitchesRemaining,
    percent,
    dailyMax: eligibility.dailyMax,
    reason: eligibility.reason,
    dailyRemaining: eligibility.pitchesRemaining,
    dailyUsed,
    eventMax,
    eventRemaining,
    eventUsed,
  };
}

export function getTournamentCapacity(
  player: Player,
  ruleset: PitchCountRuleset,
  ageGroup: string,
  currentTournamentId: string,
  enforceGlobalRest: boolean,
  referenceDate: Date = new Date(),
  customRules?: any
) {
  const ageRules = (ruleset.id === 'custom' && customRules)
    ? customRules
    : (ruleset.ageGroups[ageGroup] || ruleset.ageGroups['12U'] || { dailyMax: 85, eventMax: 100, enforceEventMax: true, restThresholds: [] });
  
  // Filter logs based on the toggle: 
  // True: Mandatory pitch rest from league games impacts tournament eligibility.
  // False: Tournament is a "Fresh Start"; only pitches thrown within this tournamentId count towards limits.
  const relevantLogs = (player.pitchingLog || [])
    .filter(l => enforceGlobalRest || l.tournamentId === currentTournamentId);

  const totalEventPitches = (player.pitchingLog || [])
    .filter(l => l.tournamentId === currentTournamentId)
    .reduce((sum, l) => sum + l.pitchCount, 0);

  const eventMax: number | null = ageRules.eventMax ?? (ageRules.enforceEventMax ? 100 : null);
  const remaining = eventMax != null ? Math.max(0, eventMax - totalEventPitches) : Infinity;

  // Determine Color for Slider
  const pct = eventMax != null ? (totalEventPitches / eventMax) * 100 : 0;
  const color = pct >= 100 ? 'black' : pct > 80 ? 'red' : pct > 50 ? 'amber' : 'green';

  // Rest day check still uses the last relevant outing
  const eligibility = getPitcherEligibility(
    relevantLogs,
    ruleset,
    ageGroup,
    referenceDate,
    0,
    currentTournamentId,
    enforceGlobalRest,
    customRules
  );

  return {
    totalEventPitches,
    remaining,
    color,
    pct,
    isCapped: eventMax != null && totalEventPitches >= eventMax,
    restStatus: eligibility
  };
}
